import { HttpStatusCode } from '@angular/common/http';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  FormsUtil,
  InputComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  TextEllipsisAppendDirective,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export type CloudRestoreAddDestinationParams = {
  bucketName: string;
  UserId: string;
  accountId: string;
  region: string;
};

export type CloudRestoreAddDestinationResponse = {
  StatusCode: HttpStatusCode;
  PayLoad: string;
};

export type CloudRestoreAddDestinationModalData = {
  UserId: string;
  accountId: string;
  region: string;
  regionName?: string;
  tryAddDestination: (params: CloudRestoreAddDestinationParams) => Observable<CloudRestoreAddDestinationResponse>;
};

const S3Regular = /^(?!\d+\.\d+\.\d+\.\d+$)(?!.*\.\.)(?!.*\.-)(?!.*-\.)([a-z0-9][a-z0-9.-]*[a-z0-9])$/;

@UntilDestroy()
@Component({
  selector: 'mbs-cloud-restore-add-destination-modal',
  imports: [
    ModalComponent,
    AlertComponent,
    ReactiveFormsModule,
    InputComponent,
    I18NextPipe,
    ButtonComponent,
    ModalFooterDirective,
    NgbTooltip,
    TextEllipsisComponent,
    TextEllipsisAppendDirective
  ],
  standalone: true,
  templateUrl: './cloud-restore-add-destination-modal.component.html'
})
export class CloudRestoreAddDestinationModalComponent implements OnInit {
  private i18Next = inject(I18NextPipe);

  public readonly popupType = MbsPopupType;
  public bucketName = new FormControl<string>('', [Validators.pattern(S3Regular), Validators.minLength(3), Validators.maxLength(63)]);
  public params: CloudRestoreAddDestinationModalData;
  public error: string = null;
  public loading = false;

  public bucketNameRules = Object.values(
    this.i18Next.transform.call(this.i18Next, 'shared:modals.addDestinationBucket.bucketNameRules.s3', { returnObjects: true })
  );

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit(): void {
    this.params = this.baseModal.data;
  }

  public add(): void {
    if (!this.bucketName.valid) {
      return void FormsUtil.triggerValidation(this.bucketName);
    }

    this.error = null;
    this.loading = true;
    this.params
      .tryAddDestination({
        UserId: this.params.UserId,
        accountId: this.params.accountId,
        region: this.params.region,
        bucketName: this.bucketName.value
      })
      .pipe(
        finalize(() => (this.loading = false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: ({ PayLoad, StatusCode }) => {
          if (StatusCode === HttpStatusCode.Ok) {
            return void this.baseModal.save(this.bucketName.value);
          }

          this.error = PayLoad;
        },
        error: (error: unknown) => {
          this.error = this.getErrorMessage(error);
        }
      });
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;

    if (error && typeof error === 'object') {
      const httpError = error as { error?: { Message?: string; message?: string } | string; message?: string };

      if (typeof httpError.error === 'string') return httpError.error;

      return httpError.error?.Message || httpError.error?.message || httpError.message || String(error);
    }

    return String(error || '');
  }

  private bucketNameValidator(control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
